import { Component, h, Prop, Element } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';

/**
 * COMPNAME is for creating a container for either a table or a figure on a journal article page. The table and/or figure itself
 * is passed in as a child element.
 */
@Component({
  tag: 'aafp-journal-article-table-and-figure',
  shadow: false,
})
export class AafpJournalArticleTableAndFigure {
  /**
   * For numbering of the table or figure within the article, e.g. "Table 1."
   */
  @Prop() numberText: string;

  /**
   * For title of the table or figure
   */
  @Prop() heading: string;

  /**
   * For the description / caption of the table or figure appearing below it
   */
  @Prop() description: string;

  /**
   * Hides the description visually but keeps the content available for screen readers
   */
  @Prop() visuallyHideDescription: boolean;

  /**
   * For the article citation (only displays when "enlarged" is true)
   */
  @Prop() citation: string;

  /**
   * For a link to a page with an "enlarged" version of the component
   */
  @Prop() enlargeUrl: string;

  /**
   * For a link to a page with an "enlarged" version of the component which opens the print dialog automatically
   */
  @Prop() printUrl: string;

  /**
   * Is the table or figure currently "enlarged" rather than being displayed in the content well? If true: the "Enlarge" button will not display and the citation will display.
   */
  @Prop() enlarged: boolean;

  /**
   * Should the window's print dialog open when the component first renders?
   */
  @Prop() openPrintDialog: boolean;

  /**
   * Applies a background color to the title based on the NNT Rating System
   */
  @Prop() nntRating: 'benefits-greater' | 'unclear-benefits' | 'no-benefits' | 'harms-greater';

  @Element() element: HTMLElement;

  componentDidLoad() {
    if (this.openPrintDialog) {
      this.printOnPageLoad();
    }
  }

  print() {
    window.print();
  }

  async printOnPageLoad() {
    if ((document as any).fonts) {
      await (document as any).fonts.ready;
    }

    if (document.readyState === 'complete') {
      this.print();
    } else {
      window.addEventListener('load', this.print, false);
    }
  }

  render() {
    return (
      <div
        class={getClassListString(
          {
            'adl-journal-article-table-and-figure--enlarged': this.enlarged,
            [`adl-journal-article-table-and-figure--${this.nntRating}`]: !!this.nntRating,
            'adl-journal-article-table-and-figure--no-title': !this.numberText && !this.heading,
          },
          'adl-journal-article-table-and-figure',
          'adl-space__sm__m-v-md adl-space__lg__m-h-md',
        )}
      >
        <div class="adl-boxed-text">
          <div class="adl-journal-article-table-and-figure__header">
            {this.numberText && (
              <p class="adl-body adl-uppercase adl-journal-article-table-and-figure__number">
                <em>{this.numberText}</em>
              </p>
            )}
            {this.heading && <div class="adl-journal-article-table-and-figure__heading" innerHTML={this.heading} />}
            <div class="adl-journal-article-table-and-figure__toolbar">
              {this.enlargeUrl && !this.enlarged && (
                <a
                  class="adl-journal-article-table-and-figure__toolbar-link"
                  rel="nofollow"
                  target="_blank"
                  href={this.enlargeUrl}
                >
                  <i class="material-icons" aria-hidden="true">
                    zoom_out_map
                  </i>
                  Enlarge
                </a>
              )}
              {this.printUrl && !this.enlarged && (
                <a
                  class="adl-journal-article-table-and-figure__toolbar-link"
                  rel="nofollow"
                  target="_blank"
                  href={this.printUrl}
                >
                  <i class="material-icons" aria-hidden="true">
                    print
                  </i>
                  Print
                </a>
              )}
              {this.enlarged && (
                <button
                  onClick={() => this.print()}
                  class="adl-journal-article-table-and-figure__toolbar-link"
                  type="button"
                >
                  <i class="material-icons" aria-hidden="true">
                    print
                  </i>
                  Print
                </button>
              )}
            </div>
          </div>
          <figure class="adl-journal-article-table-and-figure__figure">
            <slot />
            {this.description && (
              <figcaption
                class={getClassListString(
                  {
                    'adl-visually-hidden': this.visuallyHideDescription,
                  },
                  'adl-body adl-journal-article-table-and-figure__description',
                )}
                innerHTML={this.description}
              />
            )}
          </figure>
          {this.enlarged && this.citation && (
            <div
              class="adl-body adl-space__sm__m-t-md adl-journal-article-table-and-figure__citation"
              innerHTML={this.citation}
            />
          )}
        </div>
      </div>
    );
  }
}
